import styled from "styled-components";

export const LoginPage = styled("div")`
  --bg-base: #f8fafc;
  --bg-card: #ffffff;
  --accent-primary: #4f46e5;
  --accent-secondary: #7c3aed;
  --accent-hover: #4338ca;
  --accent-glow: rgba(79, 70, 229, 0.15);
  --text-primary: #0f172a;
  --text-secondary: #475569;
  --text-muted: #94a3b8;
  --border-subtle: rgba(15, 23, 42, 0.08);
  --border-accent: rgba(79, 70, 229, 0.2);

  .login-card {
      position: relative;
      z-index: 10;
      width: 100%;
      max-width: 520px;
      margin: 24px;
      padding: 56px 48px;
      background: rgba(255, 255, 255, .8);
      border: 1px solid var(--border-subtle);
      border-radius: 24px;
      text-align: center;
      box-shadow:
        0 1px 3px rgba(15, 23, 42, 0.04),
        0 6px 16px rgba(15, 23, 42, 0.06),
        0 24px 48px rgba(15, 23, 42, 0.06);
      animation: cardReveal 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      opacity: 0;
      transform: translateY(20px);
    }

    @keyframes cardReveal {
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .login-card::before {
      content: '';
      position: absolute;
      inset: -1px;
      border-radius: 25px;
      padding: 1px;
      background: linear-gradient(
        135deg,
        rgba(79, 70, 229, 0.15) 0%,
        transparent 40%,
        transparent 60%,
        rgba(124, 58, 237, 0.1) 100%
      );
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }

    /* Logo section */
    .logo-container {
      margin-bottom: 32px;
      animation: fadeUp 0.6s 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      opacity: 0;
      transform: translateY(12px);
    }

    @keyframes fadeUp {
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .logo-icon {
      width: 60px;
      height: 60px;
      margin: 0 auto 16px;
      position: relative;
    }

    .logo-icon svg {
      width: 100%;
      height: 100%;
    }

    .logo-icon::after {
      content: '';
      position: absolute;
      inset: -12px;
      background: radial-gradient(circle, var(--accent-glow) 0%, transparent 70%);
      filter: blur(16px);
      z-index: -1;
      opacity: 0.8;
    }

    .brand-name {
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--text-secondary);
    }

    /* Headline section */
    .headline-section {
      margin-bottom: 20px;
      animation: fadeUp 0.6s 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      opacity: 0;
      transform: translateY(12px);
    }

    .headline {
      font-family: 'Instrument Serif', seri !important;
      font-size: clamp(42px, 9vw, 56px);
      font-weight: 400;
      line-height: 1.05;
      color: var(--text-primary);

      .accent {
        font-family: 'Instrument Serif', serif !important;
        background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    }

    /* Description */
    .description {
      font-size: 16px;
      line-height: 1.7;
      color: var(--text-secondary);
      margin-bottom: 36px;
      animation: fadeUp 0.6s 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      opacity: 0;
      transform: translateY(12px);
    }

    /* Login button */
    .login-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      width: 100%;
      max-width: 260px;
      padding: 16px 32px;
      font-family: 'Poppins', sans-serif;
      font-size: 15px;
      font-weight: 600;
      color: white;
      background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%);
      border: none;
      border-radius: 12px;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      animation: fadeUp 0.6s 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      opacity: 0;
      transform: translateY(12px);
    }

    .login-btn::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%);
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    .login-btn:hover {
      transform: translateY(-2px);
      box-shadow:
        0 4px 12px rgba(79, 70, 229, 0.3),
        0 8px 24px rgba(79, 70, 229, 0.2);
    }

    .login-btn:hover::before {
      opacity: 1;
    }

    .login-btn:active {
      transform: translateY(0);
    }

    .login-btn svg {
      width: 18px;
      height: 18px;
      opacity: 0.9;
    }

    /* Footer section */
    .footer {
      margin-top: 40px;
      padding-top: 24px;
      border-top: 1px solid var(--border-subtle);
      animation: fadeUp 0.6s 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      opacity: 0;
      transform: translateY(12px);
    }

    .footer p {
      font-size: 13px;
      color: var(--text-muted);
      line-height: 1.6;
    }

    .footer a {
      color: var(--accent-primary);
      text-decoration: none;
      font-weight: 500;
      transition: color 0.2s ease;
    }

    .footer a:hover {
      color: var(--accent-hover);
    }

    /* Corner accents */
    .corner-accent {
      position: absolute;
      width: 60px;
      height: 60px;
      pointer-events: none;
    }

    .corner-accent.top-left {
      top: -1px;
      left: -1px;
      border-top: 1px solid var(--border-accent);
      border-left: 1px solid var(--border-accent);
      border-radius: 24px 0 0 0;
    }

    .corner-accent.bottom-right {
      bottom: -1px;
      right: -1px;
      border-bottom: 1px solid var(--border-accent);
      border-right: 1px solid var(--border-accent);
      border-radius: 0 0 24px 0;
    }

    /* Responsive adjustments */
    @media (max-width: 560px) {
      .login-card {
        padding: 40px 28px;
        margin: 16px;
      }

      .headline {
        font-size: 38px;
      }
    }
`;

export const Logo = styled("span")<{ size: string }>`
  svg {
    height: ${({ size, theme }) => {
      switch (size) {
        case "full":
          return "max-content";
        case "xl":
          return theme.font.h1;
        case "large":
          return theme.font.h2;
        case "medium":
          return theme.font.h3;
        case "small":
          return theme.font.h4;
        default:
          return theme.font.h2;
      }
    }};
    width: 100%;
  }
`;
